import { Suspense } from 'react';

import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import {
	createFileRoute,
	Link,
	Outlet,
	useChildMatches,
} from '@tanstack/react-router';

import { faWarning } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
	Box,
	Container,
	LinearProgress,
	Paper,
	Tab,
	Tabs,
} from '@mui/material';

import { Permission } from '@repo/api/auth';
import {
	claimQueries,
	useMutateClaimClose,
	useMutateClaimReopen,
} from '@repo/api/claim/claims';
import { SingleClaim } from '@repo/api/claim/claims.types';
import { questionnaireQueries } from '@repo/api/claim/questionnaire';
import { Questionnaire } from '@repo/api/claim/questionnaire.types';
import { HTTPError } from '@repo/api/client';
import { ReportStatusBody } from '@repo/api/shared/report.types';
import { useUser } from '@repo/auth';
import { ReasonDialogProvider } from '@repo/features/reasonDialog';

import { ClaimHeader } from './_claim/-components/ClaimHeader';
import { ErrorMessage } from './_claim/-components/ErrorMessage';
import { Route as ActivityRoute } from './_claim/activity';
import { Route as BusinessRoute } from './_claim/business';
import { Route as CalculationsRoute } from './_claim/calculations';
import { Route as DataRoute } from './_claim/data';
import { Route as DocumentsRoute } from './_claim/documents';
import { Route as DetailsRoute } from './_claim/index';
import { Route as ReportRoute } from './_claim/report';

function needsRefetch(claim: SingleClaim) {
	return claim.claimDocuments.some(
		(d) =>
			d.documentStatus === 'Processing' ||
			d.documentStatus === 'Document Approval Required',
	);
}

function needReportRefetch(claim: SingleClaim, reportStatus: ReportStatusBody) {
	return (
		reportStatus.status === 'NoReport' &&
		claim.claimDocuments.length > 0 &&
		claim.claimDocuments.every((d) => d.documentStatus === 'Completed')
	);
}

const isCalculationsDisabled = ({
	claim,
	permissions,
	questionnaire,
}: {
	claim: SingleClaim;
	permissions: Permission[];
	questionnaire?: Questionnaire;
}) => {
	const isDocumentCompleted =
		claim.claimDocuments.length !== 0 &&
		claim.claimDocuments.every(
			(document) => document.documentStatus === 'Completed',
		);
	const canView = permissions.includes('claim.calculation.view');

	if (!isDocumentCompleted || !canView) {
		return true;
	}

	// Tier-two calculations require verified sales data; tier one only requires
	// completed financial documents.
	if (claim.isTier2Enabled && questionnaire?.isTier2) {
		return !claim.salesVerified;
	}

	return false;
};

export const Route = createFileRoute('/_claims/claim/$claimId/_claim')({
	loader: ({ context, params }) => {
		void context.queryClient.ensureQueryData(claimQueries.id(params.claimId));
	},
	component: function ClaimPage() {
		const params = Route.useParams();

		const { permissions } = useUser();

		const {
			data: claim,
			error,
			isLoading,
		} = useQuery(
			claimQueries.id(params.claimId, {
				refetchInterval: (claim) => (needsRefetch(claim) ? 5000 : false),
			}),
		);
		const { data: questionnaire } = useQuery(
			questionnaireQueries.id(params.claimId),
		);

		const { data: reportStatus } = useQuery(
			claimQueries.reportStatus(
				params.claimId,
				permissions.includes('claim.report'),
				{
					refetchInterval: (reportStatus) =>
						claim && needReportRefetch(claim, reportStatus) ? 5000 : false,
				},
			),
		);

		const hasReport = reportStatus && reportStatus.status !== 'NoReport';

		const matchedId = useChildMatches({
			select: (match) => match[0]?.routeId,
		});

		const { mutateAsync: close, isPending: isClosing } = useMutateClaimClose(
			params.claimId,
		);
		const { mutateAsync: reopen, isPending: isReopening } =
			useMutateClaimReopen(params.claimId);

		const { data: reasons } = useSuspenseQuery(claimQueries.claimReasons());

		if (error) {
			if (error instanceof HTTPError) {
				switch (error.response.status) {
					case 404:
						return <ErrorMessage message="Claim not found" />;
					case 403:
						return (
							<ErrorMessage
								message="You do not have permission to view this claim"
								secondaryText="Please speak to your system administrator"
							/>
						);
				}
			}
			return <ErrorMessage message="Unexpected error" />;
		}

		if (isLoading) {
			return <LinearProgress />;
		}

		if (!claim || claim.stage == null) {
			return <ErrorMessage message="Claim not found" />;
		}

		return (
			<ReasonDialogProvider
				type="Claim"
				stage={claim.stage}
				reasons={reasons}
				close={async (...params) => void (await close(...params))}
				reopen={async (...params) => void (await reopen(...params))}
				isProcessing={isClosing || isReopening}
			>
				<Container
					maxWidth="lg"
					sx={{
						display: 'flex',
						flexDirection: 'column',
						gap: 2,
						flexGrow: 1,
						'& .MuiButton-root': {
							whiteSpace: 'nowrap',
						},
						mt: 2,
					}}
				>
					<ClaimHeader claimId={params.claimId} />
					<Paper
						elevation={1}
						sx={{
							flexGrow: 1,
							display: 'flex',
							flexDirection: 'column',
							backgroundColor: 'transparent',
							borderBottomLeftRadius: 6,
							borderBottomRightRadius: 6,
							boxShadow:
								'0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14)',
						}}
					>
						<Tabs
							variant="scrollable"
							value={matchedId}
							sx={{
								minHeight: '32px',
								'& .MuiTabs-flexContainer': {
									gap: 0.5,
								},
								'& .MuiTab-root': {
									backgroundColor: '#dddddd',
									color: '#424242',
									paddingBlock: 0,
									minHeight: '32px',
									fontSize: '1rem',
									fontWeight: '500',
								},
								'& .Mui-selected': {
									color: '#424242',
									backgroundColor: 'white',
								},
							}}
							slotProps={{
								indicator: {
									sx: {
										transform: 'scaleX(0.7)',
										backgroundColor: 'accent.main',
										height: '3px',
									},
								},
							}}
						>
							<Tab
								value={DetailsRoute.id}
								component={Link}
								to={DetailsRoute.to}
								replace
								label="Claim Details"
							/>
							<Tab
								value={BusinessRoute.id}
								component={Link}
								to={BusinessRoute.to}
								replace
								label="Business Questionnaire"
								disabled={!permissions.includes('claim.questionnaire.view')}
							/>
							<Tab
								value={DocumentsRoute.id}
								component={Link}
								to={DocumentsRoute.to}
								replace
								sx={(theme) => ({
									'& svg': {
										color: `color-mix(in srgb, ${theme.palette.warning.main} 70%, white)`,
									},
								})}
								label={
									(
										claim.stage === 'Active' &&
										(permissions.includes('claim.document.approve') ||
											permissions.includes('claim.document.deny')) &&
										claim.claimDocuments.some(
											(d) => d.documentStatus === 'Document Approval Required',
										)
									) ?
										<span>
											<FontAwesomeIcon icon={faWarning} /> Documents
										</span>
									:	'Documents'
								}
								disabled={
									!claim.questionnaireCompleted ||
									!permissions.includes('claim.document.view')
								}
							/>
							<Tab
								value={DataRoute.id}
								component={Link}
								to={DataRoute.to}
								replace
								label="Extracted Data"
								disabled={
									claim.claimDocuments.length === 0 ||
									claim.claimDocuments.some(
										(d) =>
											![
												'Processing',
												'Completed',
												'Category Review',
												'Validation Error',
											].includes(d.documentStatus),
									) ||
									!permissions.includes('shared.data.view')
								}
							/>
							<Tab
								value={CalculationsRoute.id}
								component={Link}
								to={CalculationsRoute.to}
								replace
								label="Calculations"
								disabled={isCalculationsDisabled({
									claim,
									permissions,
									questionnaire,
								})}
							/>
							<Tab
								value={ReportRoute.id}
								component={Link}
								to={ReportRoute.to}
								replace
								label="Report"
								disabled={
									claim.claimDocuments.length === 0 ||
									!hasReport ||
									!permissions.includes('claim.report')
								}
							/>
							<Tab
								value={ActivityRoute.id}
								component={Link}
								to={ActivityRoute.to}
								replace
								label="Activity"
								disabled={!permissions.includes('claim.activitylog.view')}
								sx={{ marginLeft: 'auto' }}
							/>
						</Tabs>
						<Box
							sx={{
								p: 2,
								flexGrow: 1,
								display: 'flex',
								flexDirection: 'column',
								backgroundColor: 'white',
								borderBottomLeftRadius: 6,
								borderBottomRightRadius: 6,
							}}
						>
							<Suspense fallback={<LinearProgress />}>
								<Outlet />
							</Suspense>
						</Box>
					</Paper>
				</Container>
			</ReasonDialogProvider>
		);
	},
});
